import React from 'react';
import {
  Box, Grid, Card, Typography, IconButton, alpha, useTheme, TextField, Chip, Tooltip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchiveIcon from '@mui/icons-material/Archive';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const fileIcon = (type, color) => {
  const props = { sx: { fontSize: 36, color } };
  switch (type) {
    case 'image':   return <ImageIcon {...props} />;
    case 'pdf':     return <PictureAsPdfIcon {...props} />;
    case 'video':   return <VideocamIcon {...props} />;
    case 'audio':   return <AudioFileIcon {...props} />;
    case 'code':    return <CodeIcon {...props} />;
    case 'model':   return <DescriptionIcon {...props} />;
    case 'archive': return <ArchiveIcon {...props} />;
    default:        return <InsertDriveFileIcon {...props} />;
  }
};

const FILE_COLORS = {
  image: '#4caf50', pdf: '#f44336', video: '#2196f3', audio: '#ff9800',
  code: '#9c27b0', model: '#795548', archive: '#607d8b', document: '#90a4ae',
};

function Item({
  item, selected, dropTarget, inlineRenameOf, anySelected, hovered,
  onClick, onDoubleClick, onContextMenu, onMoreClick, onToggleSelect, onHoverChange,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  onInlineRenameCommit, onInlineRenameCancel,
  imageUrl, darkMode,
}) {
  const theme = useTheme();
  const isFolder = item.type === 'folder';
  const editing = inlineRenameOf === item.logicalPath;
  const [draftName, setDraftName] = React.useState(item.name);
  React.useEffect(() => { if (editing) setDraftName(item.name); }, [editing, item.name]);

  // Folders share one neutral accent — the theme primary — so the grid stays
  // calm. Files keep per-type tints because that genuinely conveys content.
  const folderAccent = theme.palette.primary.main;
  const accent = isFolder ? folderAccent : (FILE_COLORS[item.fileType] || FILE_COLORS.document);
  const showCheckbox = selected || hovered || anySelected;

  return (
    <Card
      draggable={!editing}
      onMouseEnter={() => onHoverChange(item.logicalPath, true)}
      onMouseLeave={() => onHoverChange(item.logicalPath, false)}
      onDragStart={(e) => onDragStart(e, item)}
      onDragEnd={onDragEnd}
      onDragOver={isFolder ? (e) => { e.preventDefault(); onDragOver(item); } : undefined}
      onDragLeave={isFolder ? () => onDragLeave(item) : undefined}
      onDrop={isFolder ? (e) => { e.preventDefault(); onDrop(item, e); } : undefined}
      onClick={(e) => onClick(item, e)}
      onDoubleClick={() => onDoubleClick(item)}
      onContextMenu={(e) => onContextMenu(e, item)}
      elevation={0}
      sx={{
        position: 'relative', cursor: 'pointer',
        bgcolor: darkMode ? alpha('#16181d', 0.9) : '#ffffff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: dropTarget
          ? theme.palette.primary.main
          : selected
            ? alpha(theme.palette.primary.main, 0.7)
            : alpha(theme.palette.text.primary, 0.08),
        transition: 'transform .12s ease, border-color .12s ease, box-shadow .12s ease',
        boxShadow: selected
          ? `0 0 0 1px ${theme.palette.primary.main}, 0 4px 14px ${alpha(theme.palette.primary.main, 0.18)}`
          : 'none',
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: alpha(theme.palette.primary.main, 0.45),
          boxShadow: `0 4px 14px ${alpha('#000', 0.25)}`,
        },
      }}
    >
      {/* selection checkbox (top-left) */}
      <Tooltip title={selected ? 'Deselect' : 'Select'}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(item); }}
          sx={{
            position: 'absolute', top: 6, left: 6, zIndex: 2,
            opacity: showCheckbox ? 1 : 0,
            transition: 'opacity .12s ease',
            color: selected ? theme.palette.success.main : alpha(theme.palette.text.primary, 0.6),
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            '&:hover': { bgcolor: theme.palette.background.paper },
            width: 26, height: 26,
          }}
        >
          {selected ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* more-actions (top-right, hover only) */}
      <Tooltip title="More Actions">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }}
          sx={{
            position: 'absolute', top: 6, right: 6, zIndex: 2,
            opacity: hovered || selected ? 1 : 0,
            transition: 'opacity .12s ease',
            color: 'text.primary',
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            '&:hover': { bgcolor: theme.palette.background.paper },
            width: 26, height: 26,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* visual area — aspect-ratio keeps the tile from going tower-like at
          narrow widths. The `maxHeight` is a safety net: if the browser ever
          ignores aspect-ratio (e.g. inside a flex/grid quirk), the area stops
          at a sane size instead of expanding to the image's natural height. */}
      <Box sx={{
        width: '100%',
        aspectRatio: '4 / 3',
        maxHeight: 220,
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: alpha(accent, darkMode ? 0.10 : 0.06),
        borderTopLeftRadius: 8, borderTopRightRadius: 8,
        overflow: 'hidden',
      }}>
        {isFolder ? (
          hovered
            ? <FolderOpenIcon sx={{ fontSize: 56, color: accent, opacity: 0.9 }} />
            : <FolderIcon sx={{ fontSize: 56, color: accent, opacity: 0.85 }} />
        ) : item.fileType === 'image' && imageUrl ? (
          // Absolute positioning + object-fit:cover means the <img> can never
          // dictate the container's size from its intrinsic dimensions. This
          // is the bulletproof version of "fill the box, crop if needed".
          <Box
            component="img" src={imageUrl} alt={item.name} loading="lazy"
            sx={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : fileIcon(item.fileType, accent)}
      </Box>

      {/* metadata strip */}
      <Box sx={{ px: 1.25, py: 1 }}>
        {editing ? (
          <TextField
            size="small" autoFocus fullWidth value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') onInlineRenameCommit(item, draftName);
              if (e.key === 'Escape') onInlineRenameCancel();
            }}
            onBlur={() => onInlineRenameCommit(item, draftName)}
            inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }}
          />
        ) : (
          <Typography
            variant="body2" noWrap title={item.name}
            sx={{ fontWeight: 500, fontSize: 13.5, lineHeight: 1.3 }}
          >
            {item.name}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', mt: 0.25, fontSize: 11.5 }}>
          {isFolder
            ? 'Folder'
            : `${item.fileType || 'file'}${item.sizeLabel ? ` • ${item.sizeLabel}` : ''}`}
        </Typography>
      </Box>
    </Card>
  );
}

export default function GridView({
  folders, files, imageUrls,
  selectedPaths, dropTargetPath, inlineRenameOf,
  onItemClick, onItemDoubleClick, onItemContextMenu, onItemMore,
  onToggleSelect,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  onInlineRenameCommit, onInlineRenameCancel,
  darkMode,
  emptyHint,
  onUploadClick,
}) {
  const theme = useTheme();
  const isEmpty = folders.length === 0 && files.length === 0;
  const [hoveredPath, setHoveredPath] = React.useState(null);
  const onHoverChange = React.useCallback((path, on) => {
    setHoveredPath((cur) => (on ? path : (cur === path ? null : cur)));
  }, []);
  const anySelected = selectedPaths.size > 0;

  if (isEmpty) {
    return (
      <Box sx={{
        textAlign: 'center', py: 8, px: 3,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
        }}>
          <CloudUploadIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>This folder is empty</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
          {emptyHint || 'Drop files here, paste an image with ⌘V, or use the Upload button.'}
        </Typography>
      </Box>
    );
  }

  const renderSection = (title, items, kind) => {
    if (!items.length) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary"
          sx={{ display: 'block', mb: 1, letterSpacing: 0.6, fontSize: 11, fontWeight: 600 }}>
          {title} · {items.length}
        </Typography>
        <Grid container spacing={1.5}>
          {items.map((item) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}
              sx={{ minWidth: 160 }}
              key={`${kind}-${item.logicalPath}`}>

              <Item
                item={item}
                selected={selectedPaths.has(item.logicalPath)}
                anySelected={anySelected}
                hovered={hoveredPath === item.logicalPath}
                onHoverChange={onHoverChange}
                dropTarget={dropTargetPath === item.logicalPath}
                inlineRenameOf={inlineRenameOf}
                onClick={onItemClick} onDoubleClick={onItemDoubleClick} onContextMenu={onItemContextMenu}
                onMoreClick={onItemMore} onToggleSelect={onToggleSelect}
                onDragStart={onDragStart} onDragEnd={onDragEnd}
                onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                onInlineRenameCommit={onInlineRenameCommit} onInlineRenameCancel={onInlineRenameCancel}
                imageUrl={kind === 'file' ? imageUrls[item.logicalPath] : undefined}
                darkMode={darkMode}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {renderSection('Folders', folders, 'folder')}
      {renderSection('Files', files, 'file')}
    </Box>
  );
}
